import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth, useApi } from '@/context/AuthContext';
import { INDIAN_STATES, BUSINESS_TYPES, validateGSTIN, validatePAN, getErrorMessage } from '@/utils/helpers';
import { toast } from 'sonner';
import { Building2, User, Shield, Save } from 'lucide-react';

export default function SettingsPage() {
  const { business, user, updateBusiness } = useAuth();
  const api = useApi();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    gstin: '',
    pan: '',
    legal_name: '',
    business_type: '',
    industry_category: '',
    gst_scheme: 'regular',
    state_code: '',
    pincode: '',
    address: { line1: '', line2: '', city: '', state: '', pincode: '' }
  });

  useEffect(() => {
    if (business) {
      setFormData({
        name: business.name || '',
        phone: business.phone || '',
        email: business.email || '',
        gstin: business.gstin || '',
        pan: business.pan || '',
        legal_name: business.legal_name || '',
        business_type: business.business_type || '',
        industry_category: business.industry_category || '',
        gst_scheme: business.gst_scheme || 'regular',
        state_code: business.state_code || '',
        pincode: business.pincode || '',
        address: business.address || { line1: '', line2: '', city: '', state: '', pincode: '' }
      });
    }
  }, [business]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.gstin && !validateGSTIN(formData.gstin)) {
      toast.error('Invalid GSTIN format');
      return;
    }
    
    if (formData.pan && !validatePAN(formData.pan)) {
      toast.error('Invalid PAN format');
      return;
    }

    setLoading(true);
    try {
      const response = await api.put('/business', formData);
      updateBusiness(response.data);
      toast.success('Business profile updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update profile'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="settings-page">
        <div>
          <h1 className="text-2xl font-bold text-stone-900 font-['Outfit']">Settings</h1>
          <p className="text-stone-600">Manage your business profile and preferences</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Business Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-['Outfit']">
                <Building2 className="w-5 h-5 text-[#1D9E75]" />
                Business Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Business Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Acme Corporation"
                    required
                    data-testid="business-name-input"
                  />
                </div>
                <div>
                  <Label>Legal Name (as per GST)</Label>
                  <Input
                    value={formData.legal_name}
                    onChange={(e) => setFormData(prev => ({ ...prev, legal_name: e.target.value }))}
                    placeholder="Acme Corporation Pvt Ltd"
                  />
                </div>
                <div>
                  <Label>Business Type</Label>
                  <Select 
                    value={formData.business_type} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, business_type: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {BUSINESS_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Industry</Label>
                  <Input
                    value={formData.industry_category}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry_category: e.target.value }))}
                    placeholder="Software / Retail / Manufacturing"
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Tax Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-['Outfit']">
                <Shield className="w-5 h-5 text-[#1D9E75]" />
                Tax Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>GSTIN</Label>
                  <Input
                    value={formData.gstin}
                    onChange={(e) => setFormData(prev => ({ ...prev, gstin: e.target.value.toUpperCase() }))}
                    placeholder="27AABCU9603R1ZM"
                    className="font-mono"
                    data-testid="gstin-input"
                  />
                  <p className="text-xs text-stone-500 mt-1">Leave blank if not GST registered</p>
                </div>
                <div>
                  <Label>PAN</Label>
                  <Input
                    value={formData.pan}
                    onChange={(e) => setFormData(prev => ({ ...prev, pan: e.target.value.toUpperCase() }))}
                    placeholder="AABCU9603R"
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>GST Scheme</Label>
                  <Select 
                    value={formData.gst_scheme} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, gst_scheme: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular (charge and claim GST)</SelectItem>
                      <SelectItem value="composition">Composition (turnover &lt; ₹1.5 Cr)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>State</Label>
                  <Select 
                    value={formData.state_code} 
                    onValueChange={(v) => setFormData(prev => ({ ...prev, state_code: v }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {INDIAN_STATES.map(state => (
                        <SelectItem key={state.code} value={state.code}>
                          {state.code} - {state.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg font-['Outfit']">
                <User className="w-5 h-5 text-[#1D9E75]" />
                Contact Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="+91 98765 43210"
                    required
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="contact@business.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label>Address</Label>
                  <Input
                    value={formData.address?.line1 || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, line1: e.target.value } 
                    }))}
                    placeholder="Street address"
                    className="mb-2"
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <Input
                      value={formData.address?.city || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, city: e.target.value } 
                      }))}
                      placeholder="City"
                    />
                    <Input
                      value={formData.address?.state || ''}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, state: e.target.value } 
                      }))}
                      placeholder="State"
                    />
                    <Input
                      value={formData.pincode}
                      onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                      placeholder="Pincode"
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Account Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg font-['Outfit']">Account Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-3 gap-4 text-sm">
                <div>
                  <p className="text-stone-500">Plan</p>
                  <p className="font-medium capitalize" data-testid="settings-plan">{business?.plan || 'Free'}{business?.is_trial ? ` (free trial until ${new Date(business.trial_ends_at).toLocaleDateString('en-IN')})` : ''}</p>
                </div>
                <div>
                  <p className="text-stone-500">Revenue Band</p>
                  <p className="font-medium">{business?.revenue_band?.replace('_', ' ') || 'Under ₹20L'}</p>
                </div>
                <div>
                  <p className="text-stone-500">User Role</p>
                  <p className="font-medium capitalize">{user?.role || 'Founder'}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-[#1D9E75] hover:bg-[#178a65] text-white gap-2"
              data-testid="save-settings-btn"
            >
              <Save className="w-4 h-4" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
}
